import {UserKind, type Auth, type Department, type DepartmentEditationMode, type User} from 'src/typings';
import type {ModalDialogService} from 'shared/modal-dialogs';
import type {LoginDataService} from './kp-login/kp-login.data-service';
import {RegistrationOptionAction, RegistrationOptions} from './registration-options';
import type UserDataService from '../../features/user/user.data-service';
import type LoginService from './login.service';
import type FinishedResponseInteractionService from '../services/finished-response-interaction.service';
import {exists} from '../utils/custom-utils';

export default class RegistrationService {
    public static serviceName = 'registrationService';

    /*@ngInject*/
    constructor(private loginDataService: LoginDataService,
                private userDataService: UserDataService,
                private currentAuth: Auth,
                private loginService: LoginService,
                private modalDialogService: ModalDialogService,
                private finishedResponseInteractionService: FinishedResponseInteractionService) {
    }

    public async showRegistrationOptions() {
        const options = await this.getRegistrationOptions();
        return this.showOptionsForm(options);
    }

    public async getRegistrationOptions() {
        const loginView = await this.loginDataService.getLoginView();
        return new RegistrationOptions({
            credentialsRegistration: new RegistrationOptionAction(loginView.showCredentialsRegistration, () => this.addUserCredentials()),
            fullRegistration: new RegistrationOptionAction(loginView.showFullRegistration, () => this.savePersonByForm({}, null)),
            mvsLibraryRegistration: new RegistrationOptionAction(loginView.showMvsLibraryRegistration, () => this.saveLibraryByForm({}, null))
        });
    }

    private async addUserCredentials(): Promise<any> {
        try {
            await this.userDataService.step0registration({});
            await this.loginService.refresh();
            this.loginService.reloadIfChangedButNotReloaded();
        } catch (error) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(error);
        }
    }

    public async savePersonByForm(readerAccount: any, editorAccount: any): Promise<User> {
        try {
            const finishedSaveResponse = await this.userDataService.saveUser({
                id: this.currentAuth.activeUser.id,
                kind: UserKind.PERSON,
                readerAccounts: readerAccount !== null ? [readerAccount] : null,
                editorAccounts: editorAccount !== null ? [editorAccount] : null,
                active: true
            });
            await this.loginService.refresh();
            await this.finishedResponseInteractionService.showSuccessResponseInModalWindow(finishedSaveResponse);
            this.loginService.reloadIfChangedButNotReloaded();
            return finishedSaveResponse.savedObject;

        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
            throw exceptionResponse;
        }
    }

    public async savePersonWithDepartmentByForm(readerAccount: any,
                                                editorAccount: any,
                                                readableDepartmentWithEditationMode: DepartmentWithEditationMode,
                                                editableDepartmentWithEditationMode: DepartmentWithEditationMode): Promise<User> {
        try {
            const finishedSaveResponse = await this.userDataService.saveUser({
                id: this.currentAuth.activeUser.id,
                kind: UserKind.PERSON,
                readerAccounts: readerAccount !== null ? [readerAccount] : null,
                editorAccounts: editorAccount !== null ? [editorAccount] : null,
                active: true,
                readableDepartments: exists(readableDepartmentWithEditationMode) ? [readableDepartmentWithEditationMode.department] : null,
                readableDepartmentsEditationMode: readableDepartmentWithEditationMode?.departmentEditationMode ?? null,
                editableDepartments: exists(editableDepartmentWithEditationMode) ? [editableDepartmentWithEditationMode.department] : null,
                editableDepartmentsEditationMode: editableDepartmentWithEditationMode?.departmentEditationMode ?? null
            });
            await this.loginService.refresh();
            await this.finishedResponseInteractionService.showSuccessResponseInModalWindow(finishedSaveResponse);
            this.loginService.reloadIfChangedButNotReloaded();
            return finishedSaveResponse.savedObject;

        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
            throw exceptionResponse;
        }
    }

    public async saveLibraryByForm(readerAccount: any, editorAccount: any): Promise<User> {
        try {
            const finishedSaveResponse = await this.userDataService.saveUser({
                id: this.currentAuth.activeUser.id,
                kind: UserKind.LIBRARY,
                readerAccounts: readerAccount !== null ? [readerAccount] : null,
                editorAccounts: editorAccount !== null ? [editorAccount] : null,
                active: true
            });
            await this.loginService.refresh();
            await this.finishedResponseInteractionService.showSuccessResponseInModalWindow(finishedSaveResponse);
            this.loginService.reloadIfChangedButNotReloaded();
            return finishedSaveResponse.savedObject;

        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
            throw exceptionResponse;
        }
    }

    private async showOptionsForm(registrationOptions: RegistrationOptions): Promise<void> {
        if (registrationOptions.isOnlyOne()) {
            await registrationOptions.doFirstEnabled();
            return;
        }

        await this.modalDialogService.openModalWindow('registrationOptions', {registrationOptions}); // this used to return <FinishedSaveResponse<User>> TODO: check
        // return finishedResponse.savedObject;
    }
}

export interface DepartmentWithEditationMode {
    department: Department;
    departmentEditationMode: DepartmentEditationMode
}
